import { useState, useEffect } from "react";
import { Link, useLocation, Outlet } from "react-router-dom";
import { useAuth } from "../authContext";
import { useDarkMode } from "../hooks/useDarkMode";
import { cn } from "../lib/utils";
import {
  LayoutDashboard, Briefcase, Clock, Calendar, BarChart3,
  Users, User, Menu, LogOut, ChevronDown, Building2, FolderOpen,
  CheckSquare, FileText, Activity, Search, BookOpen,
  ChevronLeft, ChevronRight, Sun, Moon, ShieldCheck, GitBranch, Repeat, Shield, MessageSquareWarning,
  Settings,
} from "lucide-react";
import ReportProblemDialog from "./ReportProblemDialog";
import Lockup from "./brand/Lockup";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import BudgetAlertBell from "./BudgetAlertBell";
import GlobalSearch from "./GlobalSearch";
import TimerBar from "./TimerBar";
import { TenantOnboardingGate } from "./TenantOnboardingWizard";

// ─── Nav item ────────────────────────────────────────────────────────────────
const NavItem = ({ item, isActive, collapsed, onClick }) => {
  const link = (
    <Link
      to={item.href}
      onClick={onClick}
      data-testid={`nav-${item.name.toLowerCase().replace(/ /g, "-")}`}
      className={`flex items-center gap-2.5 h-9 rounded text-[15px] font-medium w-full px-2 transition-all duration-150 ${
        collapsed
          ? `justify-center px-0 ${isActive ? "bg-brand-sidebar-active text-brand-ink" : "text-brand-sidebar-muted hover:bg-brand-sidebar-active hover:text-brand-sidebar-ink"}`
          : isActive
            ? "bg-brand-sidebar-active text-brand-ink shadow-[inset_3px_0_0_0_var(--brand-brass)]"
            : "text-brand-sidebar-muted hover:bg-brand-sidebar-active hover:text-brand-sidebar-ink"
      }`}
    >
      <item.icon className="w-[18px] h-[18px] shrink-0" />
      {!collapsed && <span className="truncate">{item.name}</span>}
    </Link>
  );

  if (collapsed) {
    return (
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right" className="text-xs">{item.name}</TooltipContent>
      </Tooltip>
    );
  }
  return link;
};

// ─── Nav section ─────────────────────────────────────────────────────────────
const NavSection = ({ label, items, activeFn, collapsed, onNavigate }) => (
  <div>
    {!collapsed && (
      <p className="text-[11px] font-medium uppercase tracking-widest text-brand-sidebar-muted px-2 pt-2.5 pb-1 first:pt-1.5">
        {label}
      </p>
    )}
    {collapsed && <div className="pt-3 pb-1" />}
    {items.map((item) => (
      <NavItem
        key={item.href}
        item={item}
        isActive={activeFn(item.href)}
        collapsed={collapsed}
        onClick={onNavigate}
      />
    ))}
  </div>
);

// ─── Sidebar content (shared between desktop + mobile sheet) ─────────────────
const SidebarContent = ({
  user, mainNav, mgmtNav, adminNav, activeFn, collapsed, onToggleCollapse,
  onSearch, onLogout, onReport, onNavigate, showHeader = false, isDark, toggleDark,
}) => (
  <div className="flex flex-col h-full">
    {/* Header — shown only in mobile Sheet drawer */}
    {showHeader && (
      <div className="h-14 flex items-center shrink-0 border-b border-brand-sidebar-rule px-3.5 gap-2">
        <Lockup
          symbolSize={28}
          showEndorsement={false}
          variant="brassOnDark"
          wordmarkClassName="text-[17px] font-bold tracking-[-0.02em] leading-none"
          inkClassName="text-brand-sidebar-ink"
        />
      </div>
    )}
    {/* Nav */}
    <nav
      className="flex-1 overflow-y-auto py-2 flex flex-col gap-0.5 [&::-webkit-scrollbar]:hidden"
      style={{ scrollbarWidth: "none", padding: collapsed ? "8px 4px" : "8px 8px" }}
    >
      <NavSection label="Main" items={mainNav} activeFn={activeFn} collapsed={collapsed} onNavigate={onNavigate} />
      {mgmtNav.length > 0 && (
        <NavSection label="Management" items={mgmtNav} activeFn={activeFn} collapsed={collapsed} onNavigate={onNavigate} />
      )}
      {adminNav.length > 0 && (
        <NavSection label="Admin" items={adminNav} activeFn={activeFn} collapsed={collapsed} onNavigate={onNavigate} />
      )}
    </nav>

    {/* Footer */}
    <div className="border-t border-brand-sidebar-rule shrink-0" style={{ padding: collapsed ? "8px 4px" : "8px" }}>
      {/* Search shortcut */}
      {collapsed ? (
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>
            <button
              onClick={onSearch}
              className="flex items-center justify-center w-full h-8 rounded hover:bg-brand-sidebar-active transition-colors duration-150 mb-1"
              aria-label="Search"
            >
              <Search className="w-3.5 h-3.5 text-brand-sidebar-muted" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="right" className="text-xs">Search (⌘K)</TooltipContent>
        </Tooltip>
      ) : (
        <button
          onClick={onSearch}
          className="flex items-center gap-2 w-full px-2 h-8 rounded hover:bg-brand-sidebar-active transition-colors duration-150 mb-1"
          aria-label="Search"
        >
          <Search className="w-3.5 h-3.5 text-brand-sidebar-muted shrink-0" />
          <span className="text-xs text-brand-sidebar-muted flex-1 text-left">Search...</span>
          <kbd className="text-[10px] bg-brand-sidebar-active text-brand-sidebar-muted px-1 rounded font-mono leading-none py-0.5">⌘K</kbd>
        </button>
      )}

      {/* Dark mode toggle */}
      <div className="px-2 py-1.5 border-t border-brand-sidebar-rule/60">
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={toggleDark}
              aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
              className={cn(
                "w-full flex items-center rounded-md h-9 text-brand-sidebar-muted hover:text-brand-sidebar-ink hover:bg-brand-sidebar-active transition-colors",
                collapsed ? "justify-center px-0" : "gap-2.5 px-2"
              )}
            >
              {isDark ? (
                <Sun className="w-4 h-4 shrink-0" />
              ) : (
                <Moon className="w-4 h-4 shrink-0" />
              )}
              {!collapsed && (
                <span className="text-xs font-medium truncate">
                  {isDark ? "Light mode" : "Dark mode"}
                </span>
              )}
            </button>
          </TooltipTrigger>
          {collapsed && (
            <TooltipContent side="right">
              {isDark ? "Light mode" : "Dark mode"}
            </TooltipContent>
          )}
        </Tooltip>
      </div>

      {/* User row */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className={`flex items-center gap-2.5 w-full rounded hover:bg-brand-sidebar-active transition-colors duration-150 ${
              collapsed ? "justify-center px-0 h-9" : "px-2 py-2"
            }`}
            data-testid="user-menu-button"
          >
            <div className="w-8 h-8 bg-brand-sidebar-active rounded-lg flex items-center justify-center overflow-hidden shrink-0">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
              ) : (
                <span className="text-[12px] font-semibold text-brand-ink">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            {!collapsed && (
              <>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-brand-sidebar-ink truncate leading-none mb-1">{user?.name}</p>
                  {user?.role && (
                    <p className="leading-none">
                      <span className="text-[10px] bg-brand-sidebar-active text-brand-sidebar-muted px-1.5 py-0.5 rounded font-medium capitalize">
                        {user.role}
                      </span>
                    </p>
                  )}
                </div>
                <ChevronDown className="w-3 h-3 text-brand-sidebar-muted shrink-0" />
              </>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side={collapsed ? "right" : "top"} align="start" className="w-52 mb-1">
          <div className="px-3 py-2">
            <p className="text-sm font-medium text-brand-ink dark:text-brand-sidebar-ink">{user?.name}</p>
            <p className="text-xs text-brand-sidebar-muted">{user?.email}</p>
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link to="/profile" className="flex items-center gap-2 cursor-pointer" data-testid="profile-link">
              <User className="w-4 h-4" />
              Profile
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={onReport}
            className="cursor-pointer"
            data-testid="report-problem-menu-item"
          >
            <MessageSquareWarning className="w-4 h-4 mr-2" />
            Report a problem
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onLogout} className="text-red-600 cursor-pointer" data-testid="logout-menu-item">
            <LogOut className="w-4 h-4 mr-2" />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  </div>
);

// ─── Layout ───────────────────────────────────────────────────────────────────
const Layout = () => {
  const { user, logout } = useAuth();
  const { isDark, toggleDark } = useDarkMode();
  const location = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem("sidebar-collapsed") === "true"
  );

  // ⌘K / Ctrl+K keyboard shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  const toggleCollapse = () => {
    setCollapsed((v) => {
      const next = !v;
      localStorage.setItem("sidebar-collapsed", String(next));
      return next;
    });
  };

  const isActive = (href) =>
    location.pathname === href || location.pathname.startsWith(href + "/");

  const handleLogout = async () => {
    await logout();
    window.location.href = "/login";
  };

  // Nav groups
  const mainNav = [
    { name: "Dashboard", href: "/dashboard", icon: LayoutDashboard },
    { name: "Work Orders", href: "/work-orders", icon: Briefcase },
    { name: "Time Entry", href: "/time-entry", icon: Clock },
    { name: "Timesheet", href: "/timesheet", icon: Calendar },
    { name: "Recurring", href: "/recurring-entries", icon: Repeat },
    { name: "Reports", href: "/reports", icon: BarChart3 },
    { name: "GitHub Activity", href: "/github-activity", icon: GitBranch },
  ];

  const mgmtNav =
    user?.role === "admin" || user?.role === "manager"
      ? [
          { name: "Clients", href: "/clients", icon: Building2 },
          { name: "Projects", href: "/projects", icon: FolderOpen },
          { name: "Approvals", href: "/approvals", icon: CheckSquare },
          { name: "Invoices", href: "/invoices", icon: FileText },
          { name: "Team", href: "/team", icon: Activity },
        ]
      : [];

  const isPlatformOperator = user?.role === "admin" && (user?.org_id === "rygan" || user?.home_org_id === "rygan");

  const adminNav =
    user?.role === "admin"
      ? [
          { name: "Users", href: "/users", icon: Users },
          ...(isPlatformOperator ? [{ name: "Operator", href: "/operator", icon: Shield }] : []),
          { name: "Studio Settings", href: "/settings/studio", icon: Settings },
          { name: "GitHub", href: "/settings/github", icon: GitBranch },
          { name: "Service Catalog", href: "/settings/service-catalog", icon: BookOpen },
          { name: "WO Templates", href: "/settings/wo-templates", icon: FileText },
          { name: "Audit Log", href: "/audit-log", icon: ShieldCheck },
        ]
      : [];

  const sidebarProps = {
    user,
    mainNav,
    mgmtNav,
    adminNav,
    activeFn: isActive,
    collapsed,
    onToggleCollapse: toggleCollapse,
    onSearch: () => { setSearchOpen(true); setSheetOpen(false); },
    onLogout: handleLogout,
    onReport: () => { setReportOpen(true); setSheetOpen(false); },
    onNavigate: () => setSheetOpen(false),
    isDark,
    toggleDark,
  };

  return (
    <TooltipProvider>
      <div className="flex flex-col h-screen overflow-hidden">
        {/* ─── Top bar: brand section (left) + TimerBar (right) ─────────────────
            The brand area NEVER changes regardless of sidebar collapse state.
            Only the nav items below collapse to icon-only (Gmail pattern). ─ */}
        <div className="flex flex-col md:flex-row shrink-0">
          {/* Brand section — same height as TimerBar; collapse control lives here (no empty strip under the lockup). */}
          <div className="flex items-center gap-2 pl-3.5 pr-2.5 h-14 md:w-52 bg-brand-sidebar border-b border-brand-sidebar-rule shrink-0">
            {/* Mobile: hamburger opens Sheet drawer */}
            <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
              <SheetTrigger asChild>
                <button
                  className="md:hidden flex items-center justify-center w-8 h-8 rounded hover:bg-brand-sidebar-active transition-colors"
                  data-testid="mobile-menu-button"
                  aria-label="Open menu"
                >
                  <Menu className="w-5 h-5 text-brand-sidebar-muted" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-52 bg-brand-sidebar border-r border-brand-sidebar-rule [&>button]:hidden">
                <SidebarContent
                  {...sidebarProps}
                  collapsed={false}
                  onToggleCollapse={null}
                  showHeader
                />
              </SheetContent>
            </Sheet>

            <Lockup
              symbolSize={28}
              showEndorsement={false}
              variant="brassOnDark"
              className="min-w-0 flex-1"
              wordmarkClassName="text-[17px] font-bold tracking-[-0.02em] leading-none"
              inkClassName="text-brand-sidebar-ink"
            />

            {/* Budget bell — always visible */}
            {(user?.role === "admin" || user?.role === "manager") && (
              <BudgetAlertBell />
            )}

            {/* Desktop collapse — keep in the brand row so the nav column starts at MAIN */}
            <button
              type="button"
              onClick={toggleCollapse}
              className="hidden md:flex items-center justify-center w-7 h-7 rounded hover:bg-brand-sidebar-active transition-colors shrink-0"
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              data-testid="sidebar-collapse-toggle"
            >
              {collapsed ? (
                <ChevronRight className="w-3.5 h-3.5 text-brand-sidebar-muted" />
              ) : (
                <ChevronLeft className="w-3.5 h-3.5 text-brand-sidebar-muted" />
              )}
            </button>
          </div>

          {/* TimerBar — hidden for executive role (strategic read-only, no time logging) */}
          {user?.role !== "executive" && (
            <div className="flex-1 min-w-0">
              <TimerBar />
            </div>
          )}
        </div>

        {/* ─── Body: sidebar nav (desktop) + content ─── */}
        <div className="flex flex-1 min-h-0">
          {/* Desktop sidebar — nav + footer only (brand + collapse live in the top bar) */}
          <aside
            className={`hidden md:flex shrink-0 flex-col bg-brand-sidebar border-r border-brand-sidebar-rule overflow-hidden transition-[width] duration-200 ease-in-out ${
              collapsed ? "w-14" : "w-52"
            }`}
          >
            <SidebarContent {...sidebarProps} showHeader={false} />
          </aside>

          {/* Content */}
          <div className="flex-1 overflow-y-auto bg-brand-surface bg-brand-surface">
            <main><Outlet /></main>
          </div>
        </div>

        {/* Global Search palette */}
        <TenantOnboardingGate />
        <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
        <ReportProblemDialog open={reportOpen} onOpenChange={setReportOpen} />
      </div>
    </TooltipProvider>
  );
};

export default Layout;
